/**
 * Which document the desktop print button should send.
 *
 * The report page can have three different documents on screen, and the one it
 * is showing is the one that must be printed. Sending the wrong one is silent:
 * a sticker report would go to the label printer as a full A4 page and nobody
 * would see the mistake until the paper came out.
 *
 * The order here is the page's own, from `onClickPrint` — an attachment first,
 * then a sticker render, then whatever the page holds, and only then a fresh
 * render. It lives in its own file because the page cannot be mounted under
 * this project's jest (its import graph reaches `require.context`), and a rule
 * about which patient's page prints should not be the untestable part.
 */

/**
 * @returns {{kind: 'attachment'|'sticker'|'held'|'render'|'unavailable', url?: string}}
 *   `render` means nothing on screen can be reused and the caller must ask the
 *   backend for one. `unavailable` means the document this report should print
 *   is not there — not rendered yet, or no longer the one on screen — and the
 *   caller must refuse rather than send a different one.
 */
export function pickDesktopPrintSource({
  displayType,
  fileUrl,
  pdfStickerStyle,
  pdfSrc,
  hasHeldBlob,
} = {}) {
  if (displayType === 'pdfAttachment' && fileUrl) {
    return { kind: 'attachment', url: fileUrl }
  }
  // A sticker report has exactly one document. Until it is rendered the two
  // things below are both the full A4 export — the blob the page holds is
  // written only by the export effect, and a fresh render asks the backend for
  // the web layout — so falling through would put a whole page through a label
  // printer. The report is answered as having nothing to print instead.
  if (pdfStickerStyle) {
    return pdfSrc ? { kind: 'sticker', url: pdfSrc } : { kind: 'unavailable' }
  }
  // The held blob is only the document on screen while the page is still
  // showing it. A failed preview clears `fileUrl` and leaves the blob as it
  // was — the PDF-overlay path does exactly that in its catch — so the screen
  // reports an error for this patient while the page still holds the previous
  // one's report. Printing what is held there puts one patient's document in
  // front of another, and the page's own button prints a blank sheet, so
  // nothing on screen contradicts it.
  if (hasHeldBlob) {
    return fileUrl ? { kind: 'held' } : { kind: 'unavailable' }
  }
  return { kind: 'render' }
}
